import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/lib/auth';
import { ticketsAPI, usersAPI, categoriesAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  priorityColors,
  priorityBar,
  statusColors,
  statusLabels,
  formatRelativeTime,
  formatSLADue,
  formatShortDate,
  getSLAStatus,
  getInitials,
  surfaceAssignmentEmailResultWith,
  TICKET_CATEGORIES,
} from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Search,
  Plus,
  AlertTriangle,
  Inbox,
  Activity,
  AlertCircle,
  UserCheck,
  Hourglass,
  CheckCircle2,
  Trash2,
  MapPin,
  Home,
  Tag,
  Clock,
  ChevronRight,
  Eye,
  UserPlus,
  PlayCircle,
  Loader2,
  Archive,
} from 'lucide-react';

const QUICK_FILTERS = [
  { id: 'all', label: 'All', match: () => true },
  { id: 'new', label: 'New', match: (t) => t.status === 'open' && !t.assigned_agent_id },
  { id: 'assigned', label: 'Assigned', match: (t) => !!t.assigned_agent_id && t.status !== 'closed' && t.status !== 'resolved' && t.status !== 'in_progress' },
  { id: 'in_progress', label: 'In Progress', match: (t) => t.status === 'in_progress' },
  { id: 'waiting', label: 'Waiting', match: (t) => t.status === 'pending' },
  { id: 'escalated', label: 'Escalation Requested', match: (t) => !!t.escalation_requested },
  { id: 'resolved', label: 'Resolved', match: (t) => t.status === 'resolved' },
  { id: 'closed', label: 'Closed', match: (t) => t.status === 'closed' },
  { id: 'overdue', label: 'Overdue', match: (t) => getSLAStatus(t).state === 'overdue' },
];

function AssigneeBadge({ ticket, onAssignClick, canAssign }) {
  if (!ticket.assigned_agent_id) {
    return (
      <div className="flex items-center gap-2">
        <Badge
          className="bg-amber-50 text-amber-700 border border-amber-200 text-[11px] font-medium"
          aria-label="Unassigned"
          data-testid={`unassigned-badge-${ticket.id}`}
        >
          Unassigned
        </Badge>
        {canAssign && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-cyan-600 hover:bg-cyan-50"
            onClick={(e) => {
              e.stopPropagation();
              onAssignClick?.(ticket);
            }}
            data-testid={`quick-assign-${ticket.id}`}
            aria-label="Assign ticket"
          >
            <UserPlus className="w-3.5 h-3.5 mr-1" />
            Assign
          </Button>
        )}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 min-w-0" aria-label={`Assigned to ${ticket.assigned_agent_name}`}>
      <Avatar className="w-7 h-7 border border-slate-200">
        <AvatarFallback className="bg-cyan-100 text-cyan-700 text-[11px] font-semibold">
          {getInitials(ticket.assigned_agent_name)}
        </AvatarFallback>
      </Avatar>
      <div className="flex flex-col min-w-0">
        <span className="text-sm text-slate-800 font-medium truncate">{ticket.assigned_agent_name}</span>
        {(() => {
          const ids = ticket.assigned_agent_ids || [];
          const names = ticket.assigned_agent_names || [];
          const extras = ids
            .map((id, i) => ({ id, name: names[i] }))
            .filter((a) => a.id && a.id !== ticket.assigned_agent_id);
          if (!extras.length) return null;
          return (
            <span
              className="text-[10px] text-cyan-700 truncate"
              title={extras.map((a) => a.name).join(', ')}
              data-testid={`ticket-row-coassignees-${ticket.id}`}
            >
              +{extras.length} more · {extras.map((a) => (a.name || '').split(' ')[0]).slice(0, 2).join(', ')}{extras.length > 2 ? '…' : ''}
            </span>
          );
        })()}
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, tone = 'slate', onClick, testId }) {
  const tones = {
    slate: 'bg-slate-100 text-slate-700',
    blue: 'bg-blue-100 text-blue-700',
    cyan: 'bg-cyan-100 text-cyan-700',
    amber: 'bg-amber-100 text-amber-700',
    red: 'bg-red-100 text-red-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    indigo: 'bg-indigo-100 text-indigo-700',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left bg-white border border-slate-200 rounded-2xl p-4 md:p-5 shadow-sm hover:shadow-md hover:border-cyan-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 transition-all"
      data-testid={testId}
      aria-label={`${label}: ${value}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs md:text-sm font-medium text-slate-500 uppercase tracking-wide">{label}</p>
          <p className="mt-1 text-2xl md:text-3xl font-bold text-slate-900 tabular-nums">{value}</p>
        </div>
        <span className={`shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl ${tones[tone]}`}>
          <Icon className="w-5 h-5" aria-hidden="true" />
        </span>
      </div>
    </button>
  );
}

export default function TicketsView({ onSelectTicket }) {
  const { user, isAdmin, isAgent } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [statusCounts, setStatusCounts] = useState(null);  // { open, closed, … }
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [adminCategories, setAdminCategories] = useState([]);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [selectedTicketIds, setSelectedTicketIds] = useState([]);

  // Filters
  const [quickFilter, setQuickFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [filterTechnician, setFilterTechnician] = useState(user?.id || 'all'); // default: my assigned tickets
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterSite, setFilterSite] = useState('all');
  const [filterSLA, setFilterSLA] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // Lazy import to avoid circular: NewTicketDialog rendered here for direct access from this view
  const [NewTicketDialog, setNewTicketDialog] = useState(null);
  useEffect(() => {
    let mounted = true;
    import('./NewTicketDialog').then((m) => mounted && setNewTicketDialog(() => m.default));
    return () => { mounted = false; };
  }, []);

  const loadTickets = useCallback(async () => {
    try {
      setLoading(true);
      // Only fetch closed tickets when the "Closed" quick filter is active or status filter is set to closed
      const wantClosed = quickFilter === 'closed' || filterStatus === 'closed';
      const params = wantClosed ? { include_closed: true, status: 'closed' } : {};
      const res = await ticketsAPI.list(params);
      setTickets(res.data || []);
    } catch (err) {
      console.error('Failed to load tickets:', err);
    } finally {
      setLoading(false);
    }
  }, [quickFilter, filterStatus]);

  const loadAgents = useCallback(async () => {
    if (!isAgent) return;
    try {
      const [a, ad] = await Promise.all([usersAPI.list('agent'), usersAPI.list('admin')]);
      const merged = [...(a.data || []), ...(ad.data || [])];
      const seen = new Set();
      setAgents(merged.filter((u) => (seen.has(u.id) ? false : (seen.add(u.id), true))));
    } catch (err) {
      console.error('Failed to load agents:', err);
    }
  }, [isAgent]);

  useEffect(() => {
    loadTickets();
    loadAgents();
    // Always-fresh status totals (the loaded list omits closed by default).
    ticketsAPI.counts().then((r) => setStatusCounts(r.data)).catch(() => setStatusCounts(null));
    categoriesAPI.list({ type: 'ticket' })
      .then((res) => setAdminCategories((res.data || []).map((c) => c.name)))
      .catch(() => setAdminCategories([]));
  }, [loadTickets, loadAgents]);

  // Distinct sites / categories from data for filter dropdowns
  const distinctSites = useMemo(
    () => Array.from(new Set(tickets.map((t) => t.site_name).filter(Boolean))),
    [tickets]
  );
  const distinctCategories = useMemo(
    () => Array.from(new Set([...adminCategories, ...TICKET_CATEGORIES, ...tickets.map((t) => t.category).filter(Boolean)])),
    [tickets, adminCategories]
  );

  // Filtering
  const filteredTickets = useMemo(() => {
    const q = search.trim().toLowerCase();
    const qf = QUICK_FILTERS.find((f) => f.id === quickFilter);
    return tickets.filter((t) => {
      if (qf && !qf.match(t)) return false;
      if (filterStatus !== 'all' && t.status !== filterStatus) return false;
      if (filterPriority !== 'all' && t.priority !== filterPriority) return false;
      if (filterCategory !== 'all' && t.category !== filterCategory) return false;
      if (filterSite !== 'all' && t.site_name !== filterSite) return false;
      if (filterTechnician !== 'all') {
        if (filterTechnician === '__unassigned' && t.assigned_agent_id) return false;
        if (filterTechnician !== '__unassigned' && t.assigned_agent_id !== filterTechnician && !(t.assigned_agent_ids || []).includes(filterTechnician)) return false;
      }
      if (filterSLA !== 'all') {
        const s = getSLAStatus(t).state;
        if (filterSLA !== s) return false;
      }
      if (filterDateFrom && new Date(t.created_at) < new Date(filterDateFrom)) return false;
      if (filterDateTo) {
        const end = new Date(filterDateTo);
        end.setHours(23, 59, 59, 999);
        if (new Date(t.created_at) > end) return false;
      }
      if (!q) return true;
      const haystack = [
        t.ticket_number,
        t.subject,
        t.customer_name,
        t.assigned_agent_name,
        t.category,
        t.site_name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [tickets, quickFilter, search, filterStatus, filterPriority, filterCategory, filterSite, filterTechnician, filterSLA, filterDateFrom, filterDateTo]);

  // Summary metrics (computed on full set, not filtered, except date range)
  const metrics = useMemo(() => {
    const open = tickets.filter((t) => !['closed', 'resolved'].includes(t.status));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return {
      totalOpen: open.length,
      unassigned: open.filter((t) => !t.assigned_agent_id).length,
      mine: open.filter((t) => t.assigned_agent_id === user?.id || (t.assigned_agent_ids || []).includes(user?.id)).length,
      critical: open.filter((t) => t.priority === 'urgent').length,
      overdue: open.filter((t) => getSLAStatus(t).state === 'overdue').length,
      waiting: open.filter((t) => t.status === 'pending').length,
      resolvedToday: tickets.filter(
        (t) => t.status === 'resolved' && t.updated_at && new Date(t.updated_at) >= today
      ).length,
    };
  }, [tickets, user]);

  const toggleSel = (id) =>
    setSelectedTicketIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleSelAll = () =>
    setSelectedTicketIds((prev) =>
      prev.length === filteredTickets.length ? [] : filteredTickets.map((t) => t.id)
    );

  const handleBulkDelete = async () => {
    if (!selectedTicketIds.length) return;
    if (!window.confirm(`Delete ${selectedTicketIds.length} ticket(s)? This cannot be undone.`)) return;
    try {
      await ticketsAPI.bulkDelete(selectedTicketIds);
      setSelectedTicketIds([]);
      await loadTickets();
    } catch (err) {
      alert('Failed to delete tickets');
    }
  };

  const handleClaim = async (e, id) => {
    e.stopPropagation();
    try {
      await ticketsAPI.claim(id);
      await loadTickets();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to claim ticket');
    }
  };

  const surfaceAssignmentEmailResult = (data) => surfaceAssignmentEmailResultWith(toast, data);

  const handleAssignToMe = async (ticketId) => {
    try {
      const res = await ticketsAPI.update(ticketId, { assigned_agent_id: user.id });
      surfaceAssignmentEmailResult(res?.data);
      await loadTickets();
    } catch (err) {
      alert('Failed to assign');
    }
  };

  const handleStatusUpdate = async (ticketId, status) => {
    try {
      await ticketsAPI.update(ticketId, { status });
      await loadTickets();
    } catch (err) {
      alert('Failed to update status');
    }
  };

  const clearFilters = () => {
    setQuickFilter('all');
    setSearch('');
    setFilterTechnician('all');
    setFilterStatus('all');
    setFilterPriority('all');
    setFilterCategory('all');
    setFilterSite('all');
    setFilterSLA('all');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  const slaTone = (state) =>
    ({
      overdue: 'text-red-600 font-semibold',
      risk: 'text-amber-600 font-semibold',
      on_track: 'text-emerald-700',
      met: 'text-slate-500',
      none: 'text-slate-400',
    }[state] || 'text-slate-500');

  return (
    <div className="flex-1 overflow-auto bg-slate-50 min-w-0" data-testid="tickets-view">
      <div className="p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto min-w-0">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Ticketing System</h1>
            <p className="text-sm md:text-base text-slate-500 mt-1">Manage, triage, and resolve support tickets</p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && selectedTicketIds.length > 0 && (
              <Button
                variant="destructive"
                onClick={handleBulkDelete}
                className="rounded-full"
                data-testid="bulk-delete-tickets-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete ({selectedTicketIds.length})
              </Button>
            )}
            <Button
              onClick={() => setShowNewTicket(true)}
              className="rounded-full bg-cyan-500 hover:bg-cyan-600 text-white shadow-md shadow-cyan-500/30 px-5 h-11"
              data-testid="create-ticket-btn"
              aria-label="Create new ticket"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Ticket
            </Button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 md:gap-4 mb-6">
          <SummaryCard icon={Inbox} label="Total Open" value={metrics.totalOpen} tone="blue" testId="sum-open" onClick={() => { setQuickFilter('all'); setFilterStatus('all'); setFilterTechnician('all'); }} />
          <SummaryCard icon={UserPlus} label="Unassigned" value={metrics.unassigned} tone="amber" testId="sum-unassigned" onClick={() => { setQuickFilter('new'); setFilterTechnician('__unassigned'); }} />
          <SummaryCard icon={UserCheck} label="Assigned to me" value={metrics.mine} tone="cyan" testId="sum-mine" onClick={() => { setFilterTechnician(user?.id || 'all'); setQuickFilter('all'); }} />
          <SummaryCard icon={AlertTriangle} label="Critical" value={metrics.critical} tone="red" testId="sum-critical" onClick={() => { setFilterPriority('urgent'); setQuickFilter('all'); }} />
          <SummaryCard icon={AlertCircle} label="Overdue" value={metrics.overdue} tone="red" testId="sum-overdue" onClick={() => setQuickFilter('overdue')} />
          <SummaryCard icon={Hourglass} label="Waiting on user" value={metrics.waiting} tone="amber" testId="sum-waiting" onClick={() => setQuickFilter('waiting')} />
          <SummaryCard icon={CheckCircle2} label="Resolved today" value={metrics.resolvedToday} tone="emerald" testId="sum-resolved-today" onClick={() => setQuickFilter('resolved')} />
          <SummaryCard
            icon={Archive}
            label="Closed (all-time)"
            value={statusCounts?.closed ?? 0}
            tone="slate"
            testId="sum-closed"
            onClick={() => { setQuickFilter('closed'); setFilterStatus('closed'); }}
          />
        </div>

        {/* Quick filters + search */}
        <Card className="bg-white border-slate-200 mb-4 shadow-sm">
          <CardContent className="p-3 md:p-4 space-y-3">
            <div className="flex items-center gap-2 overflow-x-auto pb-1" role="tablist" aria-label="Quick ticket filters">
              {QUICK_FILTERS.map((f) => {
                const active = quickFilter === f.id;
                // Counts from the lightweight `/api/tickets/counts` endpoint.
                // `closed` is the only status that's NOT in the default list
                // response (include_closed is false by default) so we pull
                // its badge from the counts payload to avoid showing 0.
                let badge = null;
                if (f.id === 'closed' && statusCounts) badge = statusCounts.closed;
                else if (f.id === 'resolved' && statusCounts) badge = statusCounts.resolved;
                return (
                  <button
                    key={f.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setQuickFilter(f.id)}
                    className={`shrink-0 px-4 h-10 rounded-full text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 inline-flex items-center gap-1.5 ${
                      active
                        ? 'bg-slate-900 text-white shadow-sm'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                    data-testid={`qf-${f.id}`}
                  >
                    {f.label}
                    {badge != null && (
                      <span
                        className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold tabular-nums ${
                          active ? 'bg-white/20 text-white' : 'bg-white text-slate-600 border border-slate-200'
                        }`}
                        data-testid={`qf-${f.id}-count`}
                      >
                        {badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="flex flex-col md:flex-row gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" aria-hidden="true" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by ID, title, requester, technician, category, or location"
                  className="pl-10 h-11 bg-white border-slate-200"
                  data-testid="tickets-search"
                  aria-label="Search tickets"
                />
              </div>
              {(quickFilter !== 'all' || search || filterTechnician !== 'all' || filterStatus !== 'all' || filterPriority !== 'all' || filterCategory !== 'all' || filterSite !== 'all' || filterSLA !== 'all' || filterDateFrom || filterDateTo) && (
                <Button variant="outline" onClick={clearFilters} className="h-11 rounded-full" data-testid="clear-filters">
                  Clear filters
                </Button>
              )}
            </div>

            {/* Advanced filter row — wraps to 3-col grid on lg, expands to
                7-col on xl+ so the filters never push the page wider than
                the viewport on standard 1366/1440 displays. */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-2">
              {isAgent && (
                <Select value={filterTechnician} onValueChange={setFilterTechnician}>
                  <SelectTrigger className="h-10 bg-white border-slate-200" data-testid="filter-technician" aria-label="Filter by technician">
                    <SelectValue placeholder="Technician" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All technicians</SelectItem>
                    <SelectItem value="__unassigned">Unassigned</SelectItem>
                    {agents.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="h-10 bg-white border-slate-200" data-testid="filter-status" aria-label="Filter by status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="pending">Waiting</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterPriority} onValueChange={setFilterPriority}>
                <SelectTrigger className="h-10 bg-white border-slate-200" data-testid="filter-priority" aria-label="Filter by priority">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All priority</SelectItem>
                  <SelectItem value="urgent">Urgent / Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="h-10 bg-white border-slate-200" data-testid="filter-category" aria-label="Filter by category">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {distinctCategories.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterSite} onValueChange={setFilterSite}>
                <SelectTrigger className="h-10 bg-white border-slate-200" data-testid="filter-site" aria-label="Filter by site">
                  <SelectValue placeholder="Site" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sites</SelectItem>
                  {distinctSites.length === 0 && <SelectItem value="__none" disabled>(none on file)</SelectItem>}
                  {distinctSites.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterSLA} onValueChange={setFilterSLA}>
                <SelectTrigger className="h-10 bg-white border-slate-200" data-testid="filter-sla" aria-label="Filter by SLA status">
                  <SelectValue placeholder="SLA status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All SLA</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="risk">Due soon</SelectItem>
                  <SelectItem value="on_track">On track</SelectItem>
                  <SelectItem value="met">Met</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex gap-1 min-w-0">
                <Input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} className="h-10 bg-white border-slate-200 min-w-0 flex-1" aria-label="Filter from date" data-testid="filter-date-from" />
                <Input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} className="h-10 bg-white border-slate-200 min-w-0 flex-1" aria-label="Filter to date" data-testid="filter-date-to" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tickets list */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
          </div>
        ) : filteredTickets.length === 0 ? (
          <Card className="bg-white border-slate-200">
            <CardContent className="py-16 text-center text-slate-500">
              <Inbox className="w-14 h-14 mx-auto mb-3 opacity-30" />
              <p className="text-base font-medium text-slate-700">No tickets match your filters</p>
              <p className="text-sm text-slate-500 mt-1">Adjust filters or create a new ticket.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden lg:block bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="tickets-table">
                  <thead className="bg-slate-50 sticky top-0 z-10 border-b border-slate-200">
                    <tr>
                      {isAdmin && (
                        <th className="px-4 py-3 text-left">
                          <Checkbox
                            checked={selectedTicketIds.length === filteredTickets.length && filteredTickets.length > 0}
                            onCheckedChange={toggleSelAll}
                            aria-label="Select all tickets"
                          />
                        </th>
                      )}
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Ticket</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Priority</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Assigned to</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Site</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Created</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Due</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">SLA</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Updated</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredTickets.map((t) => {
                      const sla = getSLAStatus(t);
                      return (
                        <tr
                          key={t.id}
                          className="hover:bg-slate-50 cursor-pointer focus-within:bg-slate-50"
                          onClick={() => onSelectTicket?.(t)}
                          tabIndex={0}
                          onKeyDown={(e) => { if (e.key === 'Enter') onSelectTicket?.(t); }}
                          data-testid={`ticket-row-${t.id}`}
                        >
                          {isAdmin && (
                            <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedTicketIds.includes(t.id)}
                                onCheckedChange={() => toggleSel(t.id)}
                                aria-label={`Select ticket ${t.ticket_number}`}
                              />
                            </td>
                          )}
                          <td className="px-4 py-3">
                            <div className="flex items-start gap-3">
                              <span className={`mt-1 w-1 h-10 rounded-full ${priorityBar[t.priority] || 'bg-slate-300'}`} aria-hidden="true" />
                              <div className="min-w-0">
                                <div className="font-mono text-xs text-slate-500">{t.ticket_number}</div>
                                <div className="font-semibold text-slate-900 truncate max-w-xs" title={t.subject}>{t.subject}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <Badge className={`${statusColors[t.status] || statusColors.open} text-[11px] font-medium border`}>{statusLabels[t.status] || t.status}</Badge>
                              {t.escalation_requested && (
                                <Badge
                                  className="bg-red-50 text-red-700 border border-red-200 text-[10px] font-medium"
                                  data-testid={`escalation-badge-${t.id}`}
                                  title={t.escalation_reason || 'Escalation requested'}
                                >
                                  <AlertTriangle className="w-3 h-3 mr-0.5" />
                                  Escalation{t.assignment_tier ? ` → T${t.assignment_tier}` : ''}
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={`${priorityColors[t.priority]} text-[11px] font-medium border`}>{t.priority}</Badge>
                          </td>
                          <td className="px-4 py-3">
                            <AssigneeBadge ticket={t} canAssign={isAdmin} onAssignClick={() => handleAssignToMe(t.id)} />
                          </td>
                          <td className="px-4 py-3">
                            {t.category ? (
                              <span className="inline-flex items-center gap-1 text-sm text-slate-700"><Tag className="w-3.5 h-3.5 text-slate-400" />{t.category}</span>
                            ) : <span className="text-slate-400 text-sm">—</span>}
                          </td>
                          <td className="px-4 py-3">
                            {t.site_name ? (
                              <span className="inline-flex items-center gap-1 text-sm text-slate-700" data-testid={`ticket-row-site-${t.id}`}>
                                <Home className="w-3.5 h-3.5 text-slate-400" />{t.site_name}
                              </span>
                            ) : <span className="text-slate-400 text-sm">—</span>}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" title={formatRelativeTime(t.created_at)} data-testid={`ticket-created-${t.id}`}>
                            {formatShortDate(t.created_at)}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-testid={`ticket-due-${t.id}`}>
                            {formatShortDate(t.sla_due_at)}
                          </td>
                          <td className={`px-4 py-3 text-sm whitespace-nowrap ${slaTone(sla.state)}`}>
                            {formatSLADue(t)}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap" title={formatRelativeTime(t.updated_at)}>{formatShortDate(t.updated_at)}</td>
                          <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1">
                              <Button size="sm" variant="ghost" className="h-8 px-2 text-slate-600 hover:bg-slate-100" onClick={() => onSelectTicket?.(t)} data-testid={`view-${t.id}`} aria-label="View ticket">
                                <Eye className="w-4 h-4" />
                              </Button>
                              {isAgent && !t.assigned_agent_id && (
                                <Button size="sm" className="h-8 rounded-full bg-cyan-500 hover:bg-cyan-600 text-white px-3" onClick={(e) => handleClaim(e, t.id)} data-testid={`claim-${t.id}`}>
                                  Claim
                                </Button>
                              )}
                              {isAgent && t.assigned_agent_id === user?.id && t.status === 'open' && (
                                <Button size="sm" variant="outline" className="h-8 rounded-full px-3" onClick={() => handleStatusUpdate(t.id, 'in_progress')} data-testid={`start-${t.id}`}>
                                  <PlayCircle className="w-3.5 h-3.5 mr-1" />
                                  Start
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile / Tablet card view */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:hidden gap-3">
              {filteredTickets.map((t) => {
                const sla = getSLAStatus(t);
                return (
                  <div
                    role="button"
                    tabIndex={0}
                    key={t.id}
                    onClick={() => onSelectTicket?.(t)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectTicket?.(t); } }}
                    className="text-left bg-white border border-slate-200 rounded-2xl p-4 shadow-sm hover:shadow-md hover:border-cyan-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 transition-all cursor-pointer"
                    data-testid={`ticket-card-${t.id}`}
                    aria-label={`Ticket ${t.ticket_number}: ${t.subject}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`shrink-0 mt-1 w-1.5 h-12 rounded-full ${priorityBar[t.priority] || 'bg-slate-300'}`} aria-hidden="true" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-mono text-[11px] text-slate-500">{t.ticket_number}</div>
                            <h3 className="font-semibold text-slate-900 leading-tight">{t.subject}</h3>
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <Badge className={`${statusColors[t.status] || statusColors.open} text-[11px] border`}>{statusLabels[t.status] || t.status}</Badge>
                          <Badge className={`${priorityColors[t.priority]} text-[11px] border`}>{t.priority}</Badge>
                          {t.escalation_requested && (
                            <Badge
                              className="bg-red-50 text-red-700 border border-red-200 text-[11px]"
                              data-testid={`escalation-badge-card-${t.id}`}
                              title={t.escalation_reason || 'Escalation requested'}
                            >
                              <AlertTriangle className="w-3 h-3 mr-0.5" />
                              Escalation{t.assignment_tier ? ` → T${t.assignment_tier}` : ''}
                            </Badge>
                          )}
                          {t.category && (
                            <Badge variant="outline" className="text-[11px] border-slate-200 text-slate-600">{t.category}</Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-3 text-[12px]">
                          <div>
                            <p className="text-slate-500 uppercase tracking-wide text-[10px]">Requester</p>
                            <p className="text-slate-800 truncate">{t.customer_name || '—'}</p>
                          </div>
                          <div>
                            <p className="text-slate-500 uppercase tracking-wide text-[10px]">Assigned to</p>
                            <p className="text-slate-800 truncate">
                              {t.assigned_agent_name || <span className="text-amber-700 font-medium">Unassigned</span>}
                            </p>
                          </div>
                          <div>
                            <p className="text-slate-500 uppercase tracking-wide text-[10px]">Created</p>
                            <p className="text-slate-700 truncate">{formatShortDate(t.created_at)}</p>
                          </div>
                          <div>
                            <p className="text-slate-500 uppercase tracking-wide text-[10px]">Due</p>
                            <p className="text-slate-700 truncate">{formatShortDate(t.sla_due_at)}</p>
                          </div>
                          <div className={`${slaTone(sla.state)}`}>
                            <p className="text-slate-500 uppercase tracking-wide text-[10px]">SLA</p>
                            <p className="truncate">{formatSLADue(t)}</p>
                          </div>
                          <div>
                            <p className="text-slate-500 uppercase tracking-wide text-[10px]">Updated</p>
                            <p className="text-slate-700 truncate">{formatShortDate(t.updated_at)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                          <Button size="sm" variant="outline" className="h-9 rounded-full" onClick={() => onSelectTicket?.(t)} data-testid={`view-card-${t.id}`}>
                            <Eye className="w-3.5 h-3.5 mr-1" />
                            View
                          </Button>
                          {isAgent && !t.assigned_agent_id && (
                            <Button size="sm" className="h-9 rounded-full bg-cyan-500 hover:bg-cyan-600 text-white" onClick={(e) => handleClaim(e, t.id)} data-testid={`claim-card-${t.id}`}>
                              <UserPlus className="w-3.5 h-3.5 mr-1" />
                              Claim
                            </Button>
                          )}
                          {isAgent && t.assigned_agent_id === user?.id && t.status === 'open' && (
                            <Button size="sm" variant="outline" className="h-9 rounded-full" onClick={() => handleStatusUpdate(t.id, 'in_progress')} data-testid={`start-card-${t.id}`}>
                              <PlayCircle className="w-3.5 h-3.5 mr-1" />
                              Start
                            </Button>
                          )}
                          {isAgent && t.assigned_agent_id === user?.id && t.status === 'in_progress' && (
                            <Button size="sm" variant="outline" className="h-9 rounded-full" onClick={() => handleStatusUpdate(t.id, 'resolved')} data-testid={`resolve-card-${t.id}`}>
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                              Resolve
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {NewTicketDialog && (
        <NewTicketDialog
          open={showNewTicket}
          onOpenChange={setShowNewTicket}
          currentUser={user}
          onCreated={(t) => {
            loadTickets();
            onSelectTicket?.(t);
          }}
        />
      )}
    </div>
  );
}
